/**
 * What a plugin shows: a tree of plain building blocks that Nexus draws in its
 * own style. No HTML, no styles, no scripts — so a plugin cannot look like or
 * act as something else on the page.
 */

export type UiNode =
  | { type: 'stack'; children: UiNode[] }
  | { type: 'row'; children: UiNode[] }
  | { type: 'heading'; text: string }
  | { type: 'text'; text: string; muted: boolean }
  | { type: 'badge'; text: string }
  | { type: 'link'; text: string; pageId: string }
  | { type: 'list'; items: { text: string; secondary: string; pageId: string | null }[] }
  | { type: 'table'; columns: string[]; rows: string[][] }
  | { type: 'progress'; value: number; label: string }
  | { type: 'bars'; items: { label: string; value: number }[] }
  | { type: 'button'; label: string; action: string; payload: unknown }
  | {
      type: 'form';
      action: string;
      submit: string;
      fields: { name: string; label: string; kind: 'text' | 'number' | 'date' }[];
    };

const MAX_NODES = 1500;
const MAX_DEPTH = 8;
const s = (v: unknown, max = 1000) =>
  typeof v === 'string' ? v.slice(0, max) : typeof v === 'number' ? String(v) : '';
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Clean a tree a plugin returned; unknown parts are dropped, sizes are capped. */
export function sanitizeUi(raw: unknown): UiNode {
  let count = 0;
  const walk = (v: unknown, depth: number): UiNode | null => {
    if (++count > MAX_NODES || depth > MAX_DEPTH) return null;
    if (typeof v === 'string' || typeof v === 'number')
      return { type: 'text', text: s(v), muted: false };
    if (!v || typeof v !== 'object') return null;
    const n = v as Record<string, unknown>;
    const kids = () =>
      (Array.isArray(n.children) ? n.children : [])
        .map((c) => walk(c, depth + 1))
        .filter((c): c is UiNode => !!c);
    switch (n.type) {
      case 'stack':
      case 'row':
        return { type: n.type, children: kids() };
      case 'heading':
        return { type: 'heading', text: s(n.text, 200) };
      case 'text':
        return { type: 'text', text: s(n.text, 4000), muted: n.muted === true };
      case 'badge':
        return { type: 'badge', text: s(n.text, 60) };
      case 'link':
        return { type: 'link', text: s(n.text, 200), pageId: s(n.pageId, 64) };
      case 'list':
        return {
          type: 'list',
          items: (Array.isArray(n.items) ? n.items : []).slice(0, 300).map((i) => {
            const it = (i && typeof i === 'object' ? i : { text: i }) as Record<string, unknown>;
            return {
              text: s(it.text, 300),
              secondary: s(it.secondary, 200),
              pageId: typeof it.pageId === 'string' ? it.pageId.slice(0, 64) : null,
            };
          }),
        };
      case 'table': {
        const columns = (Array.isArray(n.columns) ? n.columns : [])
          .slice(0, 12)
          .map((c) => s(c, 80));
        const rows = (Array.isArray(n.rows) ? n.rows : [])
          .slice(0, 500)
          .map((r) =>
            (Array.isArray(r) ? r : []).slice(0, columns.length || 12).map((c) => s(c, 300)),
          );
        return { type: 'table', columns, rows };
      }
      case 'progress':
        return {
          type: 'progress',
          value: Math.min(1, Math.max(0, num(n.value))),
          label: s(n.label, 120),
        };
      case 'bars':
        return {
          type: 'bars',
          items: (Array.isArray(n.items) ? n.items : []).slice(0, 50).map((i) => ({
            label: s((i as Record<string, unknown>)?.label, 60),
            value: num((i as Record<string, unknown>)?.value),
          })),
        };
      case 'button':
        return {
          type: 'button',
          label: s(n.label, 60),
          action: s(n.action, 60),
          payload: n.payload ?? null,
        };
      case 'form':
        return {
          type: 'form',
          action: s(n.action, 60),
          submit: s(n.submit, 60) || 'OK',
          fields: (Array.isArray(n.fields) ? n.fields : []).slice(0, 12).map((f) => {
            const x = (f ?? {}) as Record<string, unknown>;
            const kind = x.kind === 'number' || x.kind === 'date' ? x.kind : 'text';
            return { name: s(x.name, 40), label: s(x.label, 80), kind };
          }),
        };
    }
    return null;
  };
  return walk(raw, 0) ?? { type: 'text', text: '', muted: true };
}
